import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  WorkflowEvent,
  WorkflowProgress,
  WorkflowAttemptState,
  WorkflowProviderOrderEntry,
} from '@/lib/types/workflow.types';
import type { WorkflowSnapshot } from '@/store/api/workflowApi';

/**
 * Live workflow progress, pushed by the Workflow service over the app-wide
 * Notification-Service SSE stream (see sidebar.tsx). Keyed by workflowId.
 */
export interface WorkflowProgressState {
  byWorkflowId: Record<string, WorkflowProgress>;
}

const initialState: WorkflowProgressState = { byWorkflowId: {} };

function upsertAttempt(
  attempts: Record<number, WorkflowAttemptState>,
  rank: number,
  update: Partial<WorkflowAttemptState>
): Record<number, WorkflowAttemptState> {
  const existing = attempts[rank] ?? { rank };
  return { ...attempts, [rank]: { ...existing, ...update } };
}

const workflowProgressSlice = createSlice({
  name: 'workflowProgress',
  initialState,
  reducers: {
    /**
     * Initialise a workflow entry when a POST /api/workflows response arrives
     * (before any SSE events have been received).
     */
    workflowInitialized(
      state,
      action: PayloadAction<{
        workflowId: string;
        personId: string;
        companyId: string;
        /** From the create response - the waterfall's planned provider order, the only list the modal can show pending rows from. */
        providerOrder?: WorkflowProviderOrderEntry[];
      }>
    ) {
      const { workflowId, personId, companyId, providerOrder } = action.payload;
      if (!state.byWorkflowId[workflowId]) {
        state.byWorkflowId[workflowId] = {
          workflowId,
          personId,
          companyId,
          providerOrder: providerOrder ?? [],
          currentAttemptRank: 0,
          // The waterfall always starts at rank 0, and that provider is already
          // known from this same create response - no need to wait for its
          // provider.searching SSE event just to learn who's being tried first.
          currentProviderName: providerOrder?.[0]?.providerName,
          currentProviderKey: providerOrder?.[0]?.providerKey,
          email: null,
          email_verification: null,
          completedAt: null,
          attempts: {},
        };
      } else if (providerOrder?.length) {
        // An SSE event can beat the POST response (see the skeleton in
        // workflowEventReceived, which has no provider order to work from) -
        // fill it in rather than leaving the modal with an empty list.
        const wf = state.byWorkflowId[workflowId];
        wf.providerOrder = providerOrder;
        // Same backfill as above, for the case where the skeleton beat this
        // response but no provider.searching event has landed yet either.
        if (wf.currentProviderName === undefined && wf.currentAttemptRank === 0 && wf.completedAt === null) {
          wf.currentProviderName = providerOrder[0]?.providerName;
          wf.currentProviderKey = providerOrder[0]?.providerKey;
        }
      }
    },

    /** Apply a single workflow.progress SSE event to the progress state. */
    workflowEventReceived(state, action: PayloadAction<WorkflowEvent>) {
      const evt = action.payload;
      let wf = state.byWorkflowId[evt.workflowId];
      if (!wf) {
        // May arrive before the init - bootstrap a skeleton.
        wf = {
          workflowId: evt.workflowId,
          personId: '',
          companyId: '',
          // Unknown until the POST response lands (workflowInitialized backfills it).
          providerOrder: [],
          currentAttemptRank: evt.rank ?? 0,
          currentProviderName: undefined,
          email: null,
          email_verification: null,
          completedAt: null,
          attempts: {},
        };
        state.byWorkflowId[evt.workflowId] = wf;
      }

      switch (evt.event) {
        case 'workflow.started':
          // Nothing to update beyond the skeleton above - the first real
          // progress signal is provider.searching.
          break;

        case 'provider.searching':
          // providerKey is what the modal matches on to decide which row
          // animates - set it even when `rank` is absent, so a payload without
          // one still moves the spinner onto the right provider.
          wf.currentProviderName = evt.providerName;
          wf.currentProviderKey = evt.providerKey;
          if (evt.rank != null) {
            wf.currentAttemptRank = evt.rank;
            wf.attempts = upsertAttempt(wf.attempts, evt.rank, {
              rank: evt.rank,
              providerKey: evt.providerKey,
              providerName: evt.providerName,
              dispatchState: 'dispatched',
            });
          }
          break;

        case 'verification.checking':
          // Now carries the provider's raw guess (email/email_verification)
          // alongside the dispatch state, so "View All Providers" can show
          // that guess as pending/unverified while the checkpoint runs,
          // rather than a blank row until it resolves.
          if (evt.rank != null) {
            wf.attempts = upsertAttempt(wf.attempts, evt.rank, {
              dispatchState: 'waiting_verification',
              email: evt.email ?? null,
              email_verification: evt.email_verification ?? null,
            });
          }
          break;

        case 'attempt_late_result':
          // A stale/late VERIFICATION answer for an attempt the waterfall has
          // already moved past (e.g. Infynd's guess clears the checkpoint
          // after Prospeo was already dispatched) - must NOT move
          // currentAttemptRank/currentProviderName, since the waterfall's own
          // position is unaffected. But the email/verification this event
          // carries is real, durable data about THAT specific attempt - it
          // was being discarded outright here, which is why "View All
          // Providers" showed a provider that actually found a guessed email
          // as "Not Found": nothing ever recorded that email against its row,
          // even after workflow.finished later accepted it as the overall
          // result (see the finished handler's own comment below for why that
          // doesn't cover this attempt either).
          if (evt.rank != null) {
            wf.attempts = upsertAttempt(wf.attempts, evt.rank, {
              dispatchState: 'resolved',
              email: evt.email ?? null,
              email_verification: evt.email_verification ?? null,
            });
          }
          break;

        case 'workflow.finished': {
          wf.completedAt = evt.at;
          wf.email = evt.email ?? null;
          wf.email_verification = evt.email_verification ?? null;
          wf.currentProviderName = undefined;
          wf.currentProviderKey = undefined;
          // Attribute the result to the SPECIFIC attempt that produced it.
          // `evt.rank` is the waterfall's own rank for THIS finishing event,
          // which is not necessarily the rank of whichever earlier provider's
          // guess is being accepted as the final answer (e.g. Infynd guessed
          // at rank 0, the waterfall kept trying Prospeo at rank 1, and
          // finished citing Infynd's guess - rank 1 would then wrongly get
          // marked as the row that "found" Infynd's email). Prefer an exact
          // providerKey match when the event carries one, same as
          // buildWaterfallProviderRows already does for `isCurrent` - fall
          // back to evt.rank only when no matching attempt exists yet.
          const matchedRank = evt.providerKey
            ? Object.entries(wf.attempts).find(([, a]) => a?.providerKey === evt.providerKey)?.[0]
            : undefined;
          const rank = matchedRank != null ? Number(matchedRank) : evt.rank;
          if (rank != null) {
            wf.attempts = upsertAttempt(wf.attempts, rank, {
              dispatchState: 'resolved',
              email: evt.email ?? null,
              email_verification: evt.email_verification ?? null,
            });
          }
          break;
        }
      }
    },

    /** Remove a workflow's progress (e.g. after it's been handled). */
    workflowProgressRemoved(state, action: PayloadAction<string>) {
      delete state.byWorkflowId[action.payload];
    },

    /**
     * Rebuilds one workflow's progress from a fresh GET /workflows/:id
     * snapshot, overwriting whatever was there. notifyWorkflowEvent's push to
     * Notification-Service is fire-and-forget with no retry (see that
     * service's own doc comment) - an event that's pushed while the
     * browser's SSE connection is down or mid-reconnect is dropped forever,
     * which can leave this workflow's progress permanently stuck on a stale
     * value with nothing to correct it. Dispatched on stream reconnect for
     * every workflow still in progress (see sidebar.tsx) so a dropped event
     * self-heals instead of requiring a page reload.
     */
    workflowResynced(state, action: PayloadAction<WorkflowSnapshot>) {
      const snapshot = action.payload;
      const attemptsByRank: Record<number, WorkflowAttemptState> = {};
      for (const attempt of snapshot.attempts) {
        attemptsByRank[attempt.rank] = {
          rank: attempt.rank,
          providerKey: attempt.providerKey,
          providerName: attempt.providerName,
          creditCost: attempt.creditCost,
          dispatchState: attempt.dispatchState,
          email: attempt.email,
          email_verification: attempt.email_verification,
        };
      }

      const currentAttempt = attemptsByRank[snapshot.currentRank];

      state.byWorkflowId[snapshot._id] = {
        workflowId: snapshot._id,
        personId: snapshot.personId,
        companyId: snapshot.companyId,
        providerOrder: snapshot.providerOrder ?? [],
        currentAttemptRank: snapshot.currentRank,
        currentProviderName:
          snapshot.completedAt == null && currentAttempt?.dispatchState !== 'resolved'
            ? currentAttempt?.providerName
            : undefined,
        currentProviderKey:
          snapshot.completedAt == null && currentAttempt?.dispatchState !== 'resolved'
            ? currentAttempt?.providerKey
            : undefined,
        email: snapshot.email,
        email_verification: snapshot.email_verification,
        completedAt: snapshot.completedAt,
        attempts: attemptsByRank,
      };
    },
  },
});

export const {
  workflowInitialized,
  workflowEventReceived,
  workflowProgressRemoved,
  workflowResynced,
} = workflowProgressSlice.actions;

export default workflowProgressSlice.reducer;
